// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, Mail, Paperclip, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button, Input, Select, Textarea } from '@flaircv/ui';
import type { paths } from '@flaircv/api-client';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/app/stores/auth-store';
import { useAuthConfig } from '@/app/features/auth/hooks/useAuthConfig';
import { captureViewportScreenshot } from './capture-screenshot';
import { MAX_FILES, resizeImageForUpload, resizedName } from './image-resize';
import { useFeedbackDraft, type FeedbackDraft, type FeedbackType } from './useFeedbackDraft';

const MESSAGE_MAX_LENGTH = 4000;
/** Mirrors the OpenAPI maxLength for `page_url`; longer URLs are cut rather
 *  than rejected, so a deep link never blocks a report. */
const PAGE_URL_MAX_LENGTH = 2048;

const feedbackSchema = z.object({
  type: z.enum(['bug', 'suggestion', 'other']),
  message: z.string().trim().min(1, 'Please describe your feedback.').max(MESSAGE_MAX_LENGTH),
  email: z.string().trim().email('Enter a valid email address.').max(255),
});

type FeedbackRequestBody =
  paths['/api/v1/feedback']['post']['requestBody']['content']['multipart/form-data'];

/**
 * openapi-fetch passes a FormData body through untouched instead of building
 * one from the generated body type; this narrows that escape hatch to one
 * documented cast rather than an inline `as never`.
 */
function asFeedbackRequestBody(form: FormData): FeedbackRequestBody {
  return form as unknown as FeedbackRequestBody;
}

/**
 * Floating feedback bubble (bottom-right) with a submit dialog.
 *
 * Enterprise-only: exported as `null` through the OSS feature stub, and
 * additionally hidden at runtime unless the server reports feedback_enabled.
 *
 * The form state lives in the always-mounted FeedbackBubble (useFeedbackDraft),
 * not in the dialog: dismissing it with an outside click, Escape or the ✕
 * keeps the draft (persisted to localStorage/IndexedDB), and the bubble shows
 * a red dot while an unsent draft exists. Only Send (on success) and Cancel
 * discard it.
 */
/**
 * Gate: when the server reports feedback as unavailable this component
 * returns `null` *before* mounting the draft-owning inner component, so a
 * disabled install never touches localStorage or IndexedDB.
 */
export function FeedbackBubble() {
  const { feedbackEnabled } = useAuthConfig();
  if (!feedbackEnabled) return null;
  return <FeedbackBubbleEnabled />;
}

function FeedbackBubbleEnabled() {
  const [open, setOpen] = useState(false);
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const userEmail = useAuthStore((s) => s.user?.email ?? '');
  const draft = useFeedbackDraft(userEmail, userId);

  const bubbleLabel = draft.hasDraft ? 'Send feedback (unsent draft)' : 'Send feedback';

  return (
    <>
      {/* The bubble hides while the dialog is open so it never overlaps it
          (or itself: the dialog is excluded from screenshots anyway). */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={bubbleLabel}
          title={bubbleLabel}
          className={[
            // Mobile: small, quiet, docked to the screen corner.
            'fixed z-40 flex items-center justify-center',
            'bottom-[env(safe-area-inset-bottom)] right-[env(safe-area-inset-right)] h-9 w-9 rounded-l-xl',
            'border border-r-0 border-primary bg-sidebar-accent text-black opacity-95',
            'shadow-sm transition-opacity duration-150',
            // Desktop: classic floating action button.
            'sm:bottom-5 sm:right-5 sm:h-10 sm:w-10 sm:rounded-full sm:border sm:opacity-100',
            'hover:bg-sidebar-accent/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          ].join(' ')}
        >
          <Mail className="h-[18px] w-[18px]" />
          {draft.hasDraft && (
            <span
              aria-hidden="true"
              data-draft-indicator=""
              className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-card-solid sm:-right-1 sm:-top-1"
            />
          )}
        </button>
      )}

      <FeedbackDialog
        open={open}
        draft={draft}
        onClose={() => setOpen(false)}
        onCancel={() => {
          draft.clearDraft();
          setOpen(false);
        }}
      />
    </>
  );
}

interface FeedbackDialogProps {
  open: boolean;
  draft: FeedbackDraft;
  /** Dismiss: hides the dialog, keeps the draft. */
  onClose: () => void;
  /** Cancel: wipes the form and the persisted draft, then hides the dialog. */
  onCancel: () => void;
}

function FeedbackDialog({ open, draft, onClose, onCancel }: FeedbackDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { type, message, email, attachScreenshot, files } = draft;

  // Paste an image straight from the clipboard (PrtScn → Ctrl+V). Only while
  // the dialog is open; the parent that owns the state never unmounts, so the
  // listener must not outlive the dialog.
  useEffect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const images = Array.from(e.clipboardData?.items ?? [])
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (images.length > 0) {
        e.preventDefault();
        draft.addFiles(images);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    setFieldError(null);
    const parsed = feedbackSchema.safeParse({ type, message, email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Please check the form.');
      return;
    }

    // The screenshot occupies one of the MAX_FILES slots. The UI disables the
    // checkbox at the limit and caps the picker accordingly; this is the
    // backstop so a 4-attachment request never reaches the server.
    if (attachScreenshot && files.length >= MAX_FILES) {
      setFieldError(`At most ${MAX_FILES} images can be attached.`);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('message', message.trim());
      formData.append('email', email.trim().toLowerCase());
      formData.append('page_url', window.location.href.slice(0, PAGE_URL_MAX_LENGTH));

      if (attachScreenshot) {
        const shot = await captureViewportScreenshot();
        const resized = await resizeImageForUpload(shot);
        formData.append('files', new File([resized], 'page-screenshot.jpg', { type: 'image/jpeg' }));
      }

      for (const { file } of files) {
        const resized = await resizeImageForUpload(file);
        const out =
          resized === file
            ? file
            : new File([resized], resizedName(file.name), { type: resized.type });
        formData.append('files', out);
      }

      const { error } = await apiClient.POST('/api/v1/feedback', {
        body: asFeedbackRequestBody(formData),
      });

      if (error) {
        const err = error as { error?: { message?: string } };
        setFieldError(err.error?.message ?? 'Failed to send feedback. Please try again.');
        return;
      }

      draft.clearDraft();
      toast.success('Thanks! Your feedback was sent.');
      onClose();
    } catch (e) {
      setFieldError(
        e instanceof Error ? e.message : 'Failed to send feedback. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const messageCount = message.length;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          data-feedback-ui=""
          className="fixed inset-0 z-50 bg-black/50"
        />
        <Dialog.Content
          data-feedback-ui=""
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-card-solid p-6 shadow-lg focus:outline-none"
        >
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-[15px] font-semibold text-foreground">
              Send feedback
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
            Report a bug, share a suggestion, or tell us anything else. Your draft is
            saved even if you close this window.
          </Dialog.Description>

          <form
            className="mt-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="space-y-1.5">
              <label htmlFor="feedback-type" className="text-[13px] font-medium text-foreground">
                Type
              </label>
              <Select
                id="feedback-type"
                value={type}
                onChange={(e) => draft.setType(e.target.value as FeedbackType)}
              >
                <option value="suggestion">Suggestion</option>
                <option value="bug">Bug report</option>
                <option value="other">Something else</option>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="feedback-message" className="text-[13px] font-medium text-foreground">
                Message
              </label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => draft.setMessage(e.target.value.slice(0, MESSAGE_MAX_LENGTH))}
                placeholder="What happened, what you expected, what would help…"
                rows={5}
                required
              />
              <p
                className={`text-right text-[11px] tabular-nums ${
                  messageCount >= MESSAGE_MAX_LENGTH
                    ? 'text-destructive'
                    : messageCount >= MESSAGE_MAX_LENGTH * 0.9
                      ? 'text-warning'
                      : 'text-muted-foreground'
                }`}
              >
                {messageCount}/{MESSAGE_MAX_LENGTH}
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="feedback-email" className="text-[13px] font-medium text-foreground">
                Your email
              </label>
              <Input
                id="feedback-email"
                type="email"
                value={email}
                onChange={(e) => draft.setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <label
              className={`flex items-center gap-2 text-[13px] ${
                files.length >= MAX_FILES
                  ? 'cursor-not-allowed text-muted-foreground'
                  : 'cursor-pointer text-foreground'
              }`}
            >
              <input
                type="checkbox"
                checked={attachScreenshot}
                onChange={(e) => draft.setAttachScreenshot(e.target.checked)}
                disabled={files.length >= MAX_FILES}
                className="h-4 w-4 rounded border-input accent-primary disabled:cursor-not-allowed"
              />
              Attach a screenshot of this page
            </label>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
                <Paperclip className="h-3.5 w-3.5" />
                Attachments
                <span className="font-normal text-muted-foreground">
                  ({files.length + (attachScreenshot ? 1 : 0)}/{MAX_FILES})
                </span>
              </div>

              {files.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <li key={f.previewUrl} className="relative">
                      <img
                        src={f.previewUrl}
                        alt={f.file.name}
                        className="h-16 w-16 rounded-md border border-border object-cover"
                      />
                      <button
                        type="button"
                        aria-label={`Remove ${f.file.name}`}
                        onClick={() => draft.removeFile(i)}
                        className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-card-solid p-0.5 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {files.length < MAX_FILES && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Add images (PNG, JPEG, WebP — or paste)
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  draft.addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
            </div>

            {fieldError && (
              <p role="alert" className="text-[13px] text-destructive">
                {fieldError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={submitting}>
                {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {submitting ? 'Sending…' : 'Send feedback'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
